import { track } from '../lib/events.ts';
// Общие элементы интерфейса.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Design } from '../lib/api.ts';
import { useApp } from '../lib/store.tsx';

type IconName = 'back' | 'next' | 'check' | 'close' | 'clock' | 'calendar' | 'pin' | 'phone' | 'camera' | 'user' | 'plus' | 'down' | 'heart' | 'x' | 'send' | 'grid' | 'info' | 'mail' | 'copy' | 'gift';

const PATHS: Record<IconName, ReactNode> = {
  back: <path d="M15 5l-7 7 7 7" />,
  next: <path d="M9 5l7 7-7 7" />,
  check: <path d="M5 12l5 5 9-10" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  x: <path d="M7 7l10 10M17 7L7 17" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="3" />
      <path d="M4 10h16M9 3v4M15 3v4" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  phone: <path d="M6 3h3l2 5-2.5 1.5a11 11 0 0 0 6 6L16 13l5 2v3a2 2 0 0 1-2 2A17 17 0 0 1 4 5a2 2 0 0 1 2-2z" />,
  camera: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="3" />
      <circle cx="12" cy="13" r="3.5" />
      <path d="M8 6l1.5-2h5L16 6" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  down: <path d="M6 9l6 6 6-6" />,
  heart: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />,
  send: <path d="M21 4L3 11l6 2 2 6 3-4 5 4z" />,
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="2" />
      <rect x="13" y="4" width="7" height="7" rx="2" />
      <rect x="4" y="13" width="7" height="7" rx="2" />
      <rect x="13" y="13" width="7" height="7" rx="2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.5" />
    </>
  ),
  mail: <path d="M4 5h16v11H9l-5 4z" />,
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
    </>
  ),
  gift: (
    <>
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M12 10v10M3 7h18v3H3zM12 7c-2-4-6-3-5 0M12 7c2-4 6-3 5 0" />
    </>
  ),
};

export function Icon({ name, size = 20, color = 'currentColor', width = 2 }: { name: IconName; size?: number; color?: string; width?: number }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
      {PATHS[name]}
    </svg>
  );
}

/** Пятилепестковый цветок — фирменный знак (точки прогресса, акценты). */
export function Flower({ size = 22, fill = '#FEEB9E', center }: { size?: number; fill?: string; center?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 40 40" width={size} height={size} style={{ flex: 'none' }}>
      <g fill={fill}>
        <circle cx="20" cy="10" r="7.5" />
        <circle cx="29.5" cy="16.9" r="7.5" />
        <circle cx="25.9" cy="28.1" r="7.5" />
        <circle cx="14.1" cy="28.1" r="7.5" />
        <circle cx="10.5" cy="16.9" r="7.5" />
        <circle cx="20" cy="20" r="8" />
      </g>
      {center && <circle cx="20" cy="20" r="3.5" fill={center} />}
    </svg>
  );
}

export function BackButton({ onClick }: { onClick?: () => void }) {
  const { back } = useApp();
  return (
    <button type="button" className="iconbtn" aria-label="Назад" onClick={onClick ?? back}>
      <Icon name="back" />
    </button>
  );
}

/** Верхняя строка шага: назад · цветочки прогресса · «2/4». */
export function StepTop({ step, total, gift, right }: { step: number; total: number; gift?: boolean; right?: ReactNode }) {
  const done = gift ? '#F7ACD6' : '#FEEB9E';
  const cur = gift ? '#E58FBF' : '#F3D468';
  return (
    <div className="top">
      <BackButton />
      <div className="dots" aria-label={`Шаг ${step} из ${total}`}>
        {Array.from({ length: total }, (_, i) => (i < step - 1 ? <Flower key={i} fill={done} /> : i === step - 1 ? <Flower key={i} fill={cur} /> : <i key={i} />))}
      </div>
      {right ?? <span className="step">{`${step}/${total}`}</span>}
    </div>
  );
}

export function TitleTop({ title, eyebrow, onBack }: { title: string; eyebrow?: string; onBack?: () => void }) {
  return (
    <div className="top">
      <BackButton onClick={onBack} />
      <div className="stack s6" style={{ gap: 2, minWidth: 0 }}>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1 className="h1" style={{ fontSize: eyebrow ? 26 : 28 }}>
          {title}
        </h1>
      </div>
    </div>
  );
}

const GAMMA_BG: Record<string, string> = { pastel: '#FBEFF3', warm: '#FDF3E4', white: '#EEF3E6', bright: '#FBEFF3', deep: '#F1ECF2' };

/** Фото букета, а если его ещё нет — аккуратная иллюстрация из цветов состава. */
export function BouquetArt({ design, height, tag, tagPink }: { design: Pick<Design, 'name' | 'gamma' | 'colors' | 'photoUrl'>; height: number; tag?: string; tagPink?: boolean }) {
  const [c1, c2, c3] = [design.colors[0] ?? '#F4C0D1', design.colors[1] ?? '#FBEAF0', design.colors[2] ?? '#CFC3EE'];
  const edge = (c: string) => (/^#F[0-9A-F]F[0-9A-F]F/i.test(c) ? '#E6DFD0' : 'none');
  return (
    <div className="art" style={{ height, background: GAMMA_BG[design.gamma] ?? '#FBEFF3' }}>
      {design.photoUrl ? (
        <img src={design.photoUrl} alt={`Букет «${design.name}»`} loading="lazy" />
      ) : (
        <svg role="img" aria-label={`Букет «${design.name}»`} viewBox="0 0 120 120" style={{ height: height * 0.85, width: height * 0.85 }}>
          <path d="M60 118L46 66M60 118V56M60 118L76 62" stroke="#8FA86E" strokeWidth="2" fill="none" />
          <ellipse cx="28" cy="56" rx="12" ry="5" fill="#A9BF8A" transform="rotate(-36 28 56)" />
          <ellipse cx="94" cy="50" rx="12" ry="5" fill="#A9BF8A" transform="rotate(36 94 50)" />
          {[
            [44, 40, 13, c1],
            [64, 28, 11, c2],
            [80, 44, 13, c1],
            [56, 56, 12, c3],
            [76, 64, 10, c2],
            [36, 64, 9, c3],
            [62, 42, 9, c2],
          ].map(([x, y, r, c], i) => (
            <circle key={i} cx={x as number} cy={y as number} r={r as number} fill={c as string} stroke={edge(c as string)} />
          ))}
        </svg>
      )}
      {tag && <span className={`tag${tagPink ? ' pink' : ''}`}>{tag}</span>}
    </div>
  );
}

export function Spinner({ label = 'Загружаем' }: { label?: string }) {
  return (
    <div role="status" aria-label={label} style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
      <div className="spinner" />
    </div>
  );
}

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className="switch" onClick={() => onChange(!on)} />;
}

export function Sheet({ children, onClose, label }: { children: ReactNode; onClose?: () => void; label: string }) {
  // Закрытие свайпом вниз, когда шторка прокручена до верха
  const start = useRef<number | null>(null);
  const [dy, setDy] = useState(0);
  return (
    <div className="sheet-bg" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={dy ? { transform: `translateY(${dy}px)`, transition: 'none' } : undefined}
        onTouchStart={(e) => {
          start.current = e.currentTarget.scrollTop <= 0 ? e.touches[0].clientY : null;
        }}
        onTouchMove={(e) => {
          if (start.current !== null) setDy(Math.max(0, e.touches[0].clientY - start.current));
        }}
        onTouchEnd={() => {
          if (dy > 90) onClose?.();
          start.current = null;
          setDy(0);
        }}
      >
        <span className="grip" />
        {children}
      </div>
    </div>
  );
}

let toastSet: ((s: string | null) => void) | null = null;
export function toast(msg: string) {
  toastSet?.(msg);
}
export function ToastHost() {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    toastSet = setMsg;
    return () => {
      toastSet = null;
    };
  }, []);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 2600);
    return () => clearTimeout(t);
  }, [msg]);
  return msg ? (
    <div className="toast" role="status">
      {msg}
    </div>
  ) : null;
}

export function ErrorNote({ error }: { error: string | null }) {
  // Каждая показанная ошибка пишется в аналитику (тексты ошибок наши, без личных данных).
  useEffect(() => {
    if (error) track('error_shown', { message: error.slice(0, 60) });
  }, [error]);
  if (!error) return null;
  return (
    <div className="note err" role="alert">
      <Icon name="info" size={18} />
      <span>{error}</span>
    </div>
  );
}

/** Уменьшить фото до 1600px по длинной стороне и сжать в JPEG: так оно быстро уходит даже по мобильному интернету. */
async function shrink(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, fail) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = fail;
      i.src = url;
    });
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * k);
    c.height = Math.round(img.height * k);
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** «Прикрепить фото»: одна фотография, превью и крестик. */
export function PhotoPicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const [busy, setBusy] = useState(false);
  if (value)
    return (
      <div className="row" style={{ gap: 10 }}>
        <img src={value} alt="Прикреплённое фото" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 12 }} />
        <span className="small" style={{ flex: 1 }}>
          Фото прикреплено
        </span>
        <button type="button" className="link" style={{ fontSize: 14, fontWeight: 600, minHeight: 44 }} onClick={() => onChange(null)}>
          Убрать
        </button>
      </div>
    );
  return (
    <label className="btn light" style={{ cursor: 'pointer' }}>
      <Icon name="camera" size={18} />
      {busy ? 'Загружаем…' : 'Прикрепить фото'}
      <input
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          setBusy(true);
          try {
            onChange(await shrink(f));
          } catch {
            toast('Не получилось открыть фото, попробуй другое');
          } finally {
            setBusy(false);
          }
        }}
      />
    </label>
  );
}
